import { Box, CircularProgress, Divider, SelectChangeEvent, Typography } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { ModalCloseIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import {
	cleanObject,
	deepEqual,
	generateUniqueID,
	validateEmail,
	validateFileSize,
	validatePhoneNumber,
} from '@src/helpers/Helpers';
import { CustomAlertType, Image } from '@src/interfaces/type';
import { AxiosError, AxiosResponse } from 'axios';
import React, { FC, useState } from 'react';
import styled from 'styled-components';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
`;

const StytledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 24px;
	// width: 100%;
	height: 100%;
	margin: 24px;
	@media screen and (max-width: 600px) {
		margin: 10px;
	}
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
`;

interface User {
	name: string;
	email: string;
	phoneNumber: string;
}

interface UserCreationProps {
	setApiCall: (data: boolean) => void;
	setAlert: (data: CustomAlertType) => void;
	rowData: any;
	setOpenDrawer: (data: boolean) => void;
	displayCustomError: any;
	windowDimensions?: any;
}

const UserEditable: FC<UserCreationProps> = ({
	setApiCall,
	setAlert,
	rowData,
	setOpenDrawer,
	displayCustomError,
	windowDimensions,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const [user, setUser] = useState<User>({
		name: rowData?.firstname,
		email: rowData?.email,
		phoneNumber: rowData?.telephone?.slice(2),
	});
	const initialUserData = {
		name: rowData?.firstname,
		email: rowData?.email,
		image: rowData?.image ? rowData?.image : undefined,
		accessLevel: rowData?.type?.toLowerCase() === 'user' ? '1' : '2',
	};
	const initialPhoneNumber = rowData?.telephone?.slice(2);
	const [accessLevel, setAccessLevel] = useState(
		rowData?.type?.toLowerCase() === 'user' ? '1' : '2'
	);
	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>(
		rowData?.image ? [{ id: generateUniqueID(), url: rowData?.image, hover: false }] : []
	);

	const code = '91';
	const [disable, setDisable] = useState(false);

	const fieldStyles = { width: windowDimensions?.width > 800 ? '569px' : '95vw', height: '36px' };
	const fieldStyles2 = { width: windowDimensions?.width > 800 ? '569px' : '94vw', height: '36px' };
	const checkChanges = () => {
		const updatedDataCopy = {
			name: user?.name,
			email: user?.email,
			image: images?.[0]?.url,
			accessLevel: accessLevel,
		};
		return deepEqual(initialUserData, updatedDataCopy);
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const files = event?.target?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};
	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = event.dataTransfer?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};

	const phoneNumberValidation = async () => {
		const promise = new Promise((resolve, reject) => {
			apiService.checkPhoneNumberAvailability(
				(response: AxiosResponse) => {
					resolve(response?.data?.data);
				},
				(error: AxiosError) => {
					reject(false);
				},
				code + user?.phoneNumber
			);
		});
		return promise;
	};
	const validateFields = async (user: User): Promise<string> => {
		const emailValidation = user?.email ? validateEmail(user?.email) : true;
		// let isPhoneNumberExisted = await phoneNumberValidation();
		// const isNumberUpdated = initialPhoneNumber === user?.phoneNumber;
		// const phoneNoValidation = !isNumberUpdated ? validatePhoneNumber(user?.phoneNumber) : true;
		// if (!isPhoneNumberExisted && !isNumberUpdated) {
		// 	return 'User with this Phone number already existed';
		// }

		if (emailValidation) {
			return 'success';
		} else {
			const errors = [];

			if (!emailValidation) {
				errors.push('Invalid email format');
			}

			// if (!phoneNoValidation) {
			// 	errors.push('Invalid phone number format');
			// }

			return errors.join(' and ');
		}
	};

	const disableButton = () => {
		if (
			!user?.name ||
			!user?.phoneNumber ||
			!user?.email ||
			!accessLevel ||
			disable ||
			checkChanges()
		) {
			return true;
		}
		return false;
	};

	const uploadImage = () => {
		const file = files[0];
		const formData = new FormData();
		formData.append('file', file);
		const uploadPromise: any = new Promise((resolve, reject) => {
			apiService?.uploadProfileImage(
				formData,
				(response: AxiosResponse) => {
					resolve(response?.data?.data?.url);
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong');
					reject(error);
				}
			);
		});
		return uploadPromise;
	};

	const getImageValue = (image: string) => {
		if (image) {
			return image;
		}
		if (!rowData?.image) {
			return null;
		}
		return rowData?.image && !images?.length ? null : rowData?.image;
	};

	const getPhoneNumber = (phone: string) => {
		if (phone === code + user?.phoneNumber) {
			return '';
		}
		return code + user?.phoneNumber;
	};

	const editUser = async (user: User) => {
		const profileImage = files?.length ? await uploadImage() : '';
		const validateMsg = await validateFields(user);
		const data = {
			name: user?.name,
			phone: getPhoneNumber(rowData?.phoneNumber),
			email: user?.email,
			image: getImageValue(profileImage),
			type: accessLevel === '1' ? 'USER' : 'ADMIN',
		};
		if (validateMsg === 'success') {
			setDisable(true);
			apiService.editUser(
				data,
				(response: AxiosResponse) => {
					setAlert({
						message: 'User Updated Successfully',
						severity: 'success',
					});
					setDisable(false);
					setApiCall(true);
					setOpenDrawer(false);
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong!');
					setDisable(false);
				},
				data?.phone
			);
		} else {
			displayCustomError(validateMsg);
		}
	};
	return (
		<StyledContainer>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				p={2}
				width="100%"
				height="56px"
			>
				<Typography variant="body1">Edit User Information</Typography>
				<Box display="flex" alignItems="center" gap="15px">
					<CustomButton
						title={
							disable ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Update'
							)
						}
						color="primary"
						styles={{ height: '36px', width: '80px' }}
						handleClick={() => editUser(user)}
						disabled={disableButton()}
					/>
					<Box sx={{ cursor: 'pointer' }} onClick={() => setOpenDrawer(false)}>
						<ModalCloseIcon />
					</Box>
				</Box>
			</Box>
			<Divider style={{ width: '100%' }} />
			<StytledForm>
				<LabeledTextField
					label="Full Name"
					value={user?.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						setUser({ ...user, name: e?.target?.value as string })
					}
					placeholder="e.g jerry Hatt"
					styles={fieldStyles}
					required
				/>
				<LabeledTextField
					label="Email Address"
					type="email"
					value={user?.email}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
						const enteredEmail = e?.target?.value as string;
						setUser({ ...user, email: enteredEmail });
					}}
					placeholder="e.g name@gmail.com"
					styles={fieldStyles}
					required
				/>

				<LabeledTextField
					label="Phone Number (+91)"
					value={user?.phoneNumber}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
						if (e?.target?.value?.length <= 10) {
							setUser({ ...user, phoneNumber: e?.target?.value as string });
						}
					}}
					disabled
					placeholder="e.g 234-67890"
					styles={fieldStyles}
					type="phoneNumber"
					required
				/>
				<LabeledSelectField
					label="Access Level"
					placeholder="e.g user"
					options={[
						{ value: 'user', id: '1' },
						{ value: 'admin', id: '2' },
					]}
					value={accessLevel}
					handleChange={(event: SelectChangeEvent<unknown>) => {
						const selectedValue = event.target.value as string;
						setAccessLevel(selectedValue);
					}}
					styles={fieldStyles2}
					paperProps={{
						height: 'unset !important',
					}}
					required
				/>

				<Box display="flex" flexDirection="column" gap="5px">
					<StyledLabel>Attach Image</StyledLabel>
					{images?.length ? (
						<ImageThumnail
							images={images}
							setImages={setImages}
							setFiles={setFiles}
							handleFileChange={handleFileChange}
							maxLength={1}
							style={{ width: windowDimensions?.width > 800 ? '569px' : '95vw' }}
						/>
					) : (
						<DragDrop
							handleFileChange={handleFileChange}
							handleFileDrop={handleFileDrop}
							text="Select image to upload"
							info="Upload image in png or jpg format."
							style={{ width: windowDimensions?.width > 800 ? '569px' : '95vw' }}
						/>
					)}
				</Box>
			</StytledForm>
		</StyledContainer>
	);
};

export default UserEditable;
